import { Badge, Box, Button, HStack, Icon, Menu, Portal, Table, Text } from "@chakra-ui/react";
import { HiEllipsisHorizontal, HiOutlinePlus } from "react-icons/hi2";

const headerStyle = {
    color: "text.grey",
    fontWeight: 600,
    fontSize: "xs",
    textTransform: "uppercase",
    letterSpacing: "wider",
    py: 3,
    px: 4,
    border: "none",
    whiteSpace: "nowrap",
};

const cellStyle = { py: 3, px: 4, border: "none", verticalAlign: "middle" };

const MemberActions = ({ member, onEdit, onToggle, onResend, onResetTwoFactor }) => (
    <Menu.Root positioning={{ placement: "bottom-end" }}>
        <Menu.Trigger asChild>
            <Button size="xs" variant="outlined" aria-label="Member actions" px={2}>
                <Icon fontSize={18}><HiEllipsisHorizontal /></Icon>
            </Button>
        </Menu.Trigger>
        <Portal>
            <Menu.Positioner>
                <Menu.Content bg="bg.tab" borderColor="border.primary" borderWidth={1}>
                    <Menu.Item value="edit" onClick={() => onEdit(member)} color="gray.200" _hover={{ bg: "bg.dark" }}>
                        Edit
                    </Menu.Item>
                    {member.isActive ? (
                        <Menu.Item value="deactivate" onClick={() => onToggle(member)} color="red.300" _hover={{ bg: "bg.dark" }}>
                            Deactivate
                        </Menu.Item>
                    ) : (
                        <Menu.Item value="activate" onClick={() => onToggle(member)} color="green.300" _hover={{ bg: "bg.dark" }}>
                            Activate
                        </Menu.Item>
                    )}
                    {!member.isActive && (
                        <Menu.Item value="resend" onClick={() => onResend(member)} color="gray.200" _hover={{ bg: "bg.dark" }}>
                            Resend invitation
                        </Menu.Item>
                    )}
                    {onResetTwoFactor && member.twoFactorEnabled && (
                        <Menu.Item value="reset-2fa" onClick={() => onResetTwoFactor(member)} color="gray.200" _hover={{ bg: "bg.dark" }}>
                            Reset 2FA
                        </Menu.Item>
                    )}
                </Menu.Content>
            </Menu.Positioner>
        </Portal>
    </Menu.Root>
);

const CompanyMembersTable = ({ company, onEditMember, onToggleMember, onResendInvitation, onResetTwoFactor, onAddMember }) => {
    const members = company.users ?? [];

    return (
        <Box w="full" bg="bg.secondary" borderTop="1px solid" borderColor="border.primary">
            <Box overflowX="auto">
                <Table.Root size="sm" minW="640px">
                    <Table.Header>
                        <Table.Row bg="bg.secondary">
                            {["Name", "Role", "Email", "Status"].map((c) => (
                                <Table.ColumnHeader key={c} {...headerStyle}>{c}</Table.ColumnHeader>
                            ))}
                            <Table.ColumnHeader {...headerStyle} textAlign="right">Actions</Table.ColumnHeader>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {members.length === 0 ? (
                            <Table.Row bg="bg.secondary">
                                <Table.Cell colSpan={5} {...cellStyle} textAlign="center" py={6}>
                                    <Text color="text.grey" fontSize="sm">No users match the current search or filter.</Text>
                                </Table.Cell>
                            </Table.Row>
                        ) : (
                            members.map((member) => (
                                <Table.Row
                                    key={member.id}
                                    bg={member.matched ? "bg.highlight" : "bg.secondary"}
                                    _hover={{ bg: "bg.highlight" }}
                                    transition="background 0.15s"
                                >
                                    <Table.Cell {...cellStyle}>
                                        <Text color={member.isActive ? "white" : "gray.500"} fontSize="sm" fontWeight={500}>
                                            {`${member.firstName ?? ""} ${member.lastName ?? ""}`.trim() || "—"}
                                        </Text>
                                    </Table.Cell>
                                    <Table.Cell {...cellStyle}>
                                        <Badge size="sm" variant="surface" colorPalette={member.role === "Owner" ? "purple" : "gray"}>
                                            {member.role}
                                        </Badge>
                                    </Table.Cell>
                                    <Table.Cell {...cellStyle} maxW="280px">
                                        <Text color="text.grey" fontSize="sm" truncate title={member.email}>{member.email}</Text>
                                    </Table.Cell>
                                    <Table.Cell {...cellStyle}>
                                        <HStack gap={2}>
                                            <Badge size="sm" colorPalette={member.isActive ? "green" : "red"} variant="solid">
                                                {member.isActive ? "Active" : "Inactive"}
                                            </Badge>
                                            {member.invitationPending && (
                                                <Text color="text.grey" fontSize="xs">invite pending</Text>
                                            )}
                                            {member.twoFactorEnabled && (
                                                <Badge size="sm" colorPalette="teal" variant="subtle">2FA</Badge>
                                            )}
                                        </HStack>
                                    </Table.Cell>
                                    <Table.Cell {...cellStyle} textAlign="right">
                                        <MemberActions
                                            member={member}
                                            onEdit={onEditMember}
                                            onToggle={onToggleMember}
                                            onResend={onResendInvitation}
                                            onResetTwoFactor={onResetTwoFactor}
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            ))
                        )}
                    </Table.Body>
                </Table.Root>
            </Box>
            <HStack px={4} py={3} justify="flex-end" borderTop="1px solid" borderColor="border.primary">
                <Button
                    size="xs"
                    variant="outlined"
                    onClick={() => onAddMember(company)}
                    disabled={!company.owner?.isActive}
                    title={company.owner?.isActive ? undefined : "Activate the owner account first"}
                >
                    <Icon fontSize={16}><HiOutlinePlus /></Icon>
                    <Text>Add user</Text>
                </Button>
            </HStack>
        </Box>
    );
};

export default CompanyMembersTable;
